'use client';

import { useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from 'next-themes';

type ViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => {
    ready: Promise<void>;
    finished: Promise<void>;
  };
};

const DURATION_MS = 600;

export function useThemeWaveToggle<
  T extends HTMLElement = HTMLButtonElement,
>() {
  const { resolvedTheme, setTheme } = useTheme();
  const btnRef = useRef<T>(null);
  const animatingRef = useRef(false);

  const toggleTheme = () => {
    if (animatingRef.current) return;
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    const doc = document as ViewTransitionDoc;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (!doc.startViewTransition || prefersReducedMotion || !btnRef.current) {
      setTheme(next);
      return;
    }

    animatingRef.current = true;

    const rect = btnRef.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = doc.startViewTransition(() => {
      // flushSync forces React to commit the theme swap inside the callback
      // so the View Transitions API captures the AFTER snapshot with the new
      // theme applied. Without this, React batches the state update and the
      // API sees no diff — the transition becomes an instant swap.
      flushSync(() => {
        setTheme(next);
      });
    });

    transition.ready
      .then(() => {
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${endRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: DURATION_MS,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        );
      })
      .catch(() => {});

    // `.finally` resets the guard, but if `transition.finished` rejects
    // (e.g. transition aborted mid-flight), the rejection propagates through
    // the returned promise and surfaces as `unhandledrejection`. The trailing
    // `.catch` absorbs it — we already reset the guard in `.finally`, so
    // there's nothing left to handle.
    transition.finished
      .finally(() => {
        animatingRef.current = false;
      })
      .catch(() => {});
  };

  return { btnRef, toggleTheme };
}
